import React, { useState, useEffect, useRef } from "react";
import { PiCheckCircleLight, PiFileTextLight, PiScalesLight, PiGavelLight, PiDatabaseLight, PiCheckSquareOffsetLight, PiWarningCircleLight } from "react-icons/pi";
import MiniLoadingToCheckmark from "../shared/animations/MiniLoadingToCheckmark";
import Button from "../shared/TailwindComponents/Button";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../store";
import { showModal, hideModal } from "../../store/actions/modalActions";

export interface Step {
  step: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed";
  messages: string[];
  icon?: React.ReactNode;
  elapsedSeconds?: number;
  iconIdentifier?: string;
}

interface ProgressTrackerProps {
  steps: Step[];
  isComplete: boolean;
  title?: string;
  caseId?: string;
  matterId?: string;
  correlationId?: string;
  isPreparingDoc?: boolean;
  isConnecting?: boolean;
  isRunning?: boolean;
  backendPercentage?: number;
  onCancel?: () => void;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps: incomingSteps,
  isComplete,
  title = "Creating Matter",
  caseId,
  matterId,
  correlationId,
  isConnecting,
  isPreparingDoc,
  isRunning,
  backendPercentage = 0,
  onCancel,
}) => {
  const [percentage, setPercentage] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPreparationPhase, setIsPreparationPhase] = useState(true);
  const processedEventIds = useRef<Set<string>>(new Set());
  const location = useLocation();
  const { t } = useTranslation();

  // For storage key, use correlationId if available (for matter), otherwise use caseId
  let storageKey = '';
  if (matterId && correlationId) {
    // Matter creation: use correlationId for storage key (matches AddMatterForm storage)
    storageKey = `matter_creation_start:${correlationId}`;
  } else if (caseId) {
    // Case creation: use caseId for storage key
    storageKey = `case_creation_start:${caseId}`;
  }

  const [steps, setSteps] = useState<Step[]>(incomingSteps);
  const dispatch = useAppDispatch();

  const pretty = (s: number) =>
    s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;

  // Helper: Check if a step is a preparation/welcome step
  const isPreparationStep = (step: Step): boolean => {
    const stepNameLower = step.step.toLowerCase();
    return stepNameLower.includes('welcome') ||
           stepNameLower.includes('preparing') ||
           stepNameLower.includes('connecting') ||
           stepNameLower.includes('initialization');
  };

  // Helper: Check if we should consider this step as processing phase
  const hasActualProcessingSteps = (stepsArray: Step[]): boolean => {
    return stepsArray.some(step => !isPreparationStep(step));
  };

  useEffect(() => {
    // Dedupe: Only update if steps content actually changed
    // Use setSteps with a deduplication check
    setSteps((prevSteps) => {
      // If the incoming steps are the exact same, don't update
      if (prevSteps.length === incomingSteps.length &&
          prevSteps.every((prev, idx) =>
            prev.step === incomingSteps[idx].step &&
            prev.status === incomingSteps[idx].status &&
            JSON.stringify(prev.messages) === JSON.stringify(incomingSteps[idx].messages)
          )) {
        return prevSteps;
      }
      return incomingSteps;
    });

    if (incomingSteps.length > 0) {
      setIsConnected(true);

      // Check if we should transition out of preparation phase
      if (isPreparationPhase && hasActualProcessingSteps(incomingSteps)) {
        console.log('[PROGRESS_TRACKER] Transitioning from preparation to processing phase');
        setIsPreparationPhase(false);
      }
    }
  }, [incomingSteps, isPreparationPhase]);

  useEffect(() => {
    // Start the timer if any processing is active
    const shouldTrackTime = isConnected || isPreparingDoc || isConnecting || isRunning;

    if (!shouldTrackTime) return;

    console.log('[ELAPSED_TIME] Starting timer', {
      storageKey,
      shouldTrackTime,
      isConnected,
      isPreparingDoc,
      isConnecting,
      isRunning
    });

    // Calculate initial elapsed time
    const stored = localStorage.getItem(storageKey);
    console.log('[ELAPSED_TIME] Retrieved stored start time:', { storageKey, stored });

    if (stored) {
      try {
        const startTimeMs = new Date(stored).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTimeMs) / 1000);

        // Sanity check: if elapsed is negative or > 1 hour (3600s), log it for debugging
        if (elapsed < 0 || elapsed > 3600) {
          console.warn('[ELAPSED_TIME] Unusual elapsed time calculated', {
            stored,
            startTimeMs,
            now,
            elapsed,
            storageKey
          });
        } else {
          console.log('[ELAPSED_TIME] Initial elapsed time set:', { elapsed, storageKey });
        }

        setElapsedTime(Math.max(0, elapsed)); // Ensure non-negative
      } catch (e) {
        console.error('[ELAPSED_TIME] Failed to parse stored time:', stored, e);
        setElapsedTime(0);
      }
    } else {
      console.warn('[ELAPSED_TIME] No stored start time found in localStorage for key:', storageKey);
      setElapsedTime(0);
    }

    // Update elapsed time every second
    const interval = setInterval(() => {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const startTimeMs = new Date(stored).getTime();
          const now = Date.now();
          const elapsed = Math.floor((now - startTimeMs) / 1000);
          setElapsedTime(Math.max(0, elapsed)); // Ensure non-negative
        } catch (e) {
          console.error('[ELAPSED_TIME] Failed to parse stored time in interval:', stored, e);
        }
      }
    }, 1000);

    return () => {
      console.log('[ELAPSED_TIME] Clearing timer interval for key:', storageKey);
      clearInterval(interval);
    };
  }, [isConnected, isPreparingDoc, isConnecting, isRunning, storageKey, isComplete]);

  useEffect(() => {
    if (!isConnected && !isPreparingDoc && !isConnecting && !isRunning) {
      setPercentage(0);
      return;
    }

    // During preparation phase, keep percentage at 0
    if (isPreparationPhase) {
      setPercentage(0);
      return;
    }

    // Prefer backend percentage if available, fallback to step-based calculation
    if (backendPercentage > 0) {
      setPercentage(backendPercentage);
    } else {
      // Filter out preparation steps from progress calculation
      const processingSteps = steps.filter(step => !isPreparationStep(step));
      const totalSteps = processingSteps.length;
      const completedSteps = processingSteps.filter((step) => step.status === "Completed").length;
      const failedSteps = processingSteps.filter((step) => step.status === "Failed").length;

      if (isComplete || failedSteps > 0) {
        setPercentage(100);
      } else {
        setPercentage(totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0);
      }
    }
  }, [steps, isComplete, isConnected, isPreparingDoc, isConnecting, isRunning, backendPercentage, isPreparationPhase]);

  const handleCancelAnalysis = async () => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Cancel Analysis'),
      subMsg: t('This will stop the current process and any data created during this process may be deleted.'),
      icon: <PiWarningCircleLight className="w-6 h-6 text-orange-600" />,
      isDest: true,
      onConfirm: () => {
        dispatch(hideModal());
        if (onCancel) {
          onCancel();
        }
      },
      onCancel: () => dispatch(hideModal())
    }));
  };

  const getIconForStep = (stepName: string, status: string): React.ReactNode => {
    if (status === "Completed") return <PiCheckCircleLight className="h-5 w-5 text-green-500" />;
    if (status === "In Progress") return <MiniLoadingToCheckmark isLoading={true} />;
    if (status === "Failed") return <PiFileTextLight className="h-5 w-5 text-red-500" />;

    const lowerStep = stepName.toLowerCase();

    // Icon mapping for STEP_SEQUENCE steps (Pending/other states)
    if (lowerStep.includes("collecting") && lowerStep.includes("context"))
      return <PiDatabaseLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("organizing") && lowerStep.includes("data"))
      return <PiFileTextLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("optimizing") && lowerStep.includes("data"))
      return <PiDatabaseLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("analysing") && lowerStep.includes("legal"))
      return <PiScalesLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("generating") && lowerStep.includes("task"))
      return <PiCheckSquareOffsetLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("final") && lowerStep.includes("evaluation"))
      return <PiGavelLight className="h-5 w-5 text-gray-400" />;

    // Backend step names (from matters.py STEP_SEQUENCE)
    if (lowerStep.includes("validate") || lowerStep.includes("datasource"))
      return <PiFileTextLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("extract") || lowerStep.includes("metadata"))
      return <PiFileTextLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("retrieve") && lowerStep.includes("documents"))
      return <PiDatabaseLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("retrieve") && (lowerStep.includes("laws") || lowerStep.includes("cases")))
      return <PiScalesLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("generate") && lowerStep.includes("tasks"))
      return <PiCheckSquareOffsetLight className="h-5 w-5 text-gray-400" />;
    if (lowerStep.includes("compile") || lowerStep.includes("results"))
      return <PiGavelLight className="h-5 w-5 text-gray-400" />;

    // Fallback cases for other step names
    switch (lowerStep) {
      case "prepare contents":
      case "generate information document":
        return <PiFileTextLight className="h-5 w-5 text-gray-400" />;
      case "retrieve laws":
      case "applicable_laws_summary":
      case "applicable_laws":
        return <PiScalesLight className="h-5 w-5 text-gray-400" />;
      case "retrieve cases":
      case "relevant_cases_summary":
      case "relevant_cases":
        return <PiScalesLight className="h-5 w-5 text-gray-500" />;
      case "create folder":
      case "save matter":
        return <PiFileTextLight className="h-5 w-5 text-gray-500" />;
      case "suggest relevant tasks":
      case "adversarial_debate":
      case "legal_analysis":
      case "interpretation":
      case "risk_assessment":
      case "legal_strategy":
      case "next_steps":
        return <PiScalesLight className="h-5 w-5 text-gray-500" />;
      case "compiling document":
        return <PiGavelLight className="h-5 w-5 text-gray-500" />;
      default:
        return <PiFileTextLight className="h-5 w-5 text-gray-500" />;
    }
  };


  // Always show the full progress view with steps - no simplified loader
  // Steps are pre-populated with "Pending" status if not yet updated

  return (
    <div className="w-[50vw] max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {isComplete ? (
            <PiCheckCircleLight className="h-6 w-6 text-green-500" />
          ) : (
            <MiniLoadingToCheckmark isLoading={true} />
          )}
          {isComplete ? t("Matter Creation Complete") : title}
        </h3>
        <div className="flex items-center gap-3">
          <Button
            variant="neutral"
            size="small"
            onClick={handleCancelAnalysis}
            className="relative"
          >
            {t("Cancel")}
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
          <div
            style={{ width: `${percentage}%` }}
            className="h-full bg-gradient-to-r from-blue-500 to-blue-900 transition-all duration-700 ease-in-out"
          />
        </div>
        <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">
          {Math.round(percentage)}% {t("Complete")}
        </span>
      </div>

      {/* Steps with vertical line */}
      <div className="relative">
        {/* Subtle vertical connector line behind icons */}
        <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gray-200 dark:bg-gray-700 opacity-50" />
        
        <div className="space-y-4 pl-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="animate-fade-in-up relative"
              style={{ animationDelay: `${index * 0.06}s` }}
            >
              {/* Step indicator dot with background to cover line */}
              <div className="absolute -left-8 top-0.5 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-full">
                {step.icon || getIconForStep(step.step, step.status)}
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <p className="font-medium capitalize">{step.step.replace(/_/g, " ")}</p>
                <span
                  className={
                    "ml-auto text-[10px] px-2 py-0.5 rounded-full " +
                    (step.status === "Completed"
                      ? "bg-green-100 text-green-700"
                      : step.status === "Failed"
                      ? "bg-red-100 text-red-700"
                      : step.status === "In Progress"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600")
                  }
                >
                  {step.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      {!isComplete ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          {t("This may take a few minutes. You can close this window.")}
        </p>
      ) : (
        <p className="text-xs text-green-700 dark:text-green-400 mt-4">
          {t("Matter created successfully")}
        </p>
      )}
    </div>
  );
};

export default ProgressTracker;
